import { useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const presetAmounts = [10, 25, 50];

export default function DonationSection() {
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useScrollAnimation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));
    setSubmitting(false);
    setSubmitted(true);
  };

  const activeAmount = customAmount ? Number(customAmount) : selectedAmount;

  return (
    <section
      id="donate"
      className="relative min-h-screen flex items-center py-20 md:py-0 px-4 md:px-8 lg:px-12"
    >
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1cd5f0-f0ba-469d-9d15-862b5fbc0c17_Home-banner---BOGI-HEALTH-ORGANIZATION-FOR-DEVELOPMENT---BHODEV.jpeg?v=5c484f002e213a147440e9f2b5e81a21"
          alt="Donation background"
          title="BHODEV Donation Campaign - Fight Against Hunger"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-dark/75"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left text */}
        <div className="text-white animate-on-scroll">
          <span className="px-4 py-1.5 rounded-full bg-white/10 text-primary text-xs font-inter font-semibold mb-6 inline-block tracking-wide uppercase backdrop-blur-sm">
            Donate Now
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-saria font-bold mb-6 leading-tight">
            Join the Fight Against Hunger
          </h2>
          <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mb-8">
            Join the fight against hunger and make a difference in the lives of millions. Every contribution counts, whether it&apos;s donating food, volunteering your time, or raising awareness about food insecurity. Together, we can create a world where no one has to go to bed hungry.
          </p>

          {/* Impact highlights */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: "ri-restaurant-line", text: "Feed a family for a week" },
              { icon: "ri-medicine-bottle-line", text: "Provide medical supplies" },
              { icon: "ri-book-open-line", text: "Support a child&apos;s education" },
              { icon: "ri-home-smile-line", text: "Rebuild community shelters" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                <div className="w-9 h-9 flex items-center justify-center bg-primary/20 rounded-lg">
                  <i className={`${item.icon} text-primary text-lg`}></i>
                </div>
                <span className="text-sm text-white/80">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Donation Form */}
        <div className="animate-on-scroll animate-on-scroll-delay-1">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            {submitted ? (
              <div className="flex flex-col items-center justify-center gap-5 py-8 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-secondary rounded-full">
                  <i className="ri-heart-fill text-4xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-saria font-bold text-dark">Thank You!</h3>
                <p className="text-stone-500 text-sm max-w-xs">Your generous donation will make a real difference in our community.</p>
                <button
                  onClick={() => { setSubmitted(false); setName(""); setEmail(""); setCustomAmount(""); setSelectedAmount(10); }}
                  className="bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-primary/20"
                >
                  Donate Again
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="text-center mb-2">
                  <h3 className="text-xl font-saria font-bold text-dark">Make a Donation</h3>
                  <p className="text-stone-400 text-sm mt-1">Choose an amount to support our cause</p>
                </div>

                {/* Amount presets */}
                <div className="grid grid-cols-3 gap-3">
                  {presetAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => { setSelectedAmount(amount); setCustomAmount(""); }}
                      className={`h-14 rounded-xl font-bold text-lg transition-all duration-300 cursor-pointer border-2 ${
                        selectedAmount === amount && !customAmount
                          ? "bg-primary text-white border-primary shadow-lg shadow-primary/20"
                          : "bg-stone-50 text-dark border-stone-200 hover:border-primary hover:text-primary"
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>

                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-lg font-semibold">$</span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(0); }}
                    placeholder="Custom amount"
                    min="1"
                    className="w-full border-2 border-stone-200 h-14 pl-10 pr-4 rounded-xl focus:border-primary focus:outline-none text-dark text-base font-semibold bg-stone-50 transition-colors"
                  />
                </div>

                {/* Name */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-stone-400"></i>
                  </div>
                  <input
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border-2 border-stone-200 h-14 pl-12 pr-4 rounded-xl focus:border-primary focus:outline-none text-dark text-sm bg-stone-50 transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-stone-400"></i>
                  </div>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border-2 border-stone-200 h-14 pl-12 pr-4 rounded-xl focus:border-primary focus:outline-none text-dark text-sm bg-stone-50 transition-colors"
                    placeholder="Your email address"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || (!customAmount && !selectedAmount)}
                  className="bg-primary text-white py-4 rounded-xl font-inter font-semibold hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 shadow-lg shadow-primary/25 hover:shadow-primary/40 text-base"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-heart-fill"></i>
                      Donate ${activeAmount > 0 ? activeAmount : "--"}
                    </span>
                  )}
                </button>

                <p className="text-center text-xs text-stone-400">
                  <i className="ri-shield-check-line mr-1"></i>
                  Secure donation. Your information is protected.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}